import React, { useCallback, useState, useEffect, memo } from 'react';
import Modal from 'react-modal';
import { AnimatedValue } from 'react-spring';
// import io from 'socket.io-client';
import Select from 'react-select';
import { BiTrashAlt } from 'react-icons/bi';
import { RGBColor } from 'react-color';

import { useFight } from '../../hooks/Fight';
import { useSocketV1Instance } from '../../hooks';
import { sendAction } from '../../services/actions/action-sender';

import { FightAction } from '../../models/fight';
import labels from '../../utils/labels';
import plurals from '../../utils/plurals';
import { toLegacyFightId } from '../../utils/legacyId';
import { Container, Header, HeaderText, CloseModalButton } from './styles';
import Icon from '../Icon';
import UpdateActionForm from './Update-Action-Form';
import { isRedCornerAction } from '../../utils/fightResult';
import { toast } from 'react-toastify';
import io from 'socket.io-client';

interface LogLineProps {
  key?: string;
  action: FightAction;
  style?: AnimatedValue<React.CSSProperties>;
  redCornerColor: RGBColor;
  blueCornerColor: RGBColor;
  isFinishedFight: boolean;
}

interface ActionById {
  _id?: string;
}

const significantStrikesNames = [
  'significantStrikeLegs',
  'significantStrikeHead',
  'significantStrikeBody',
];

const domainSwitch = (domain: boolean | null) => {
  if (domain === null) {
    return 'action-domain neutral-color';
  }
  if (domain) {
    return 'action-domain true-color';
  }
  return 'action-domain false-color';
};

const stanceSwitch = (stance: string | null) => {
  if (stance === null) {
    return 'action-domain neutral-color';
  }
  switch (stance) {
    case 'standing':
      return 'action-stance standing-color';
    case 'fence':
      return 'action-stance fence-color';
    case 'ground':
      return 'action-stance ground-color';
    default:
      return 'action-stance neutral-color';
  }
};

// const socket = io(process.env.REACT_APP_MIKE_API);

const LogLine: React.FC<LogLineProps> = ({
  action,
  style,
  children,
  redCornerColor,
  blueCornerColor,
  isFinishedFight,
}) => {
  const { fight, translateRoundTime } = useFight();

  // const { emit, isConnected } = useSocketV1Instance();

  const [isRoundAction, setIsRoundAction] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState(false);

  const handleModalOpen = useCallback(() => {
    if (isFinishedFight) {
      setIsOpen(true);
    }
  }, [isFinishedFight]);
  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (['roundEnd', 'roundStart'].includes(action.name)) {
      setIsRoundAction(true);
    }
  }, [action.name, action.round]);

  const isRedCorner = isRedCornerAction(action, fight);

  const cornerColor = isRedCorner ? redCornerColor : blueCornerColor;
  const handleRemoveAction = useCallback(
    async (actionToRemove: FightAction | undefined) => {
      if (actionToRemove) {
        if (!action?._id || !action?.name) {
          return;
        }
        const actionsToRevoke: Array<ActionById | FightAction> = [
          {
            _id: actionToRemove._id,
            eventId: actionToRemove.eventId,
            boutId: actionToRemove.boutId,
            corner: actionToRemove.corner,
            stance: actionToRemove.stance,
            fightId: actionToRemove.fightId,
            authorId: actionToRemove.authorId,
            name: actionToRemove.name,
          },
        ];

        if (significantStrikesNames.includes(action?.name)) {
          // if it's a significant strike (head | body | legs), a significant strike and a total strike has to be removed as well
          actionsToRevoke.push({
            name: 'significantStrike',
            fightId: toLegacyFightId(fight?.legacyId),
            fightNewId: fight?.id,
            round: fight.currentRound || 0,
            roundTime: actionToRemove.roundTime,
            stance: actionToRemove.stance,
            authorId: actionToRemove.authorId,
            targetId: actionToRemove.targetId,
            eventId: actionToRemove.eventId,
            boutId: actionToRemove.boutId,
            corner: actionToRemove.corner,
            domain: actionToRemove.domain,
          });

          actionsToRevoke.push({
            name: 'totalStrike',
            fightId: toLegacyFightId(fight?.legacyId),
            fightNewId: fight?.id,
            round: fight.currentRound || 0,
            roundTime: actionToRemove.roundTime,
            stance: actionToRemove.stance,
            authorId: actionToRemove.authorId,
            targetId: actionToRemove.targetId,
            eventId: actionToRemove.eventId,
            boutId: actionToRemove.boutId,
            corner: actionToRemove.corner,
            domain: actionToRemove.domain,
          });
        }

        // Sequencial: a ordem importa, e o sender já cuida de outbox/retry.
        for (const actionToRevoke of actionsToRevoke) {
          await sendAction('revoke', {
            promotionId: fight.promotionId,
            data: actionToRevoke,
          });
        }
      }
    },
    [action, fight],
  );
  const handleUpdateBodyPartAction = useCallback(
    async (value: any, actionToUpdate: FightAction | undefined) => {
      if (actionToUpdate) {
        if (!action?._id || !action?.name) {
          return;
        }
        let actionUpdated: FightAction;
        if (actionToUpdate.hasOwnProperty('extraFields')) {
          actionUpdated = actionToUpdate;
        } else {
          actionUpdated = {
            ...actionToUpdate,
            extraFields: { bodyPart: value.value },
          };
        }

        await sendAction('update', {
          promotionId: fight.promotionId,
          data: actionUpdated,
        });
      }
    },
    [action, fight],
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Fight Update Time Modal"
      >
        <Header>
          <HeaderText>
            Atualize a action
            {action.name}
          </HeaderText>
          <CloseModalButton onClick={handleCloseModal}>X</CloseModalButton>
        </Header>
        <UpdateActionForm
          action={action}
          handleCloseModal={handleCloseModal}
        />
      </Modal>
      <Container
        style={style}
        isredcorner={isRedCorner ? 'true' : 'false'}
        r={cornerColor.r}
        g={cornerColor.g}
        b={cornerColor.b}
        isroundaction={isRoundAction ? 'true' : 'false'}
      >
        <div className="logLine">
          <span>{children}</span>

          {action._id && fight && (
            <>
              <div className="time">
                <span>{translateRoundTime(action.roundTime || 0)}</span>
              </div>

              <div className="corner">
              </div>

              <div
                role="button"
                className="action-name"
                onClick={() => handleModalOpen()}
              >
                <span>
                  {isRoundAction
                    ? `${labels[plurals[action.name]]} ${action.round}`
                    : labels[plurals[action.name]]}
                </span>
                {/* <span>{action.name}</span> */}
              </div>

              <div className={stanceSwitch(action?.stance)}>
                {/* {`(${action.stance})`} */}
              </div>

              <div className={domainSwitch(action?.domain)}>
                {/* {action.domain === null
                ? '[neutral]'
                : `[${String(action.domain)}]`} */}
              </div>
              <Icon onClick={() => handleRemoveAction(action)}>
                <BiTrashAlt color="white" />
              </Icon>
            </>
          )}
          {action._id === undefined && (
            <div className="action-empty">
              <span>Aguardando ações da luta...</span>
            </div>
          )}
        </div>
        <div className="selectPart">
          {action && action.name === 'submissionAttempt' ? (
            <>
              <Select
                placeholder="Bodypart"
                onChange={(e) => handleUpdateBodyPartAction(e, action)}
                options={[
                  { value: 'Neck', label: 'Neck ' },
                  { value: 'Right arm', label: 'Right arm' },
                  { value: 'Left arm', label: 'Left arm' },
                  { value: 'Right leg', label: 'Right leg' },
                  { value: 'Left leg', label: 'Left leg' },
                ]}
              />
            </>
          ) : null}
        </div>
      </Container>
    </>
  );
};

export default memo(LogLine, (prevProps, nextProps) => {
  return (
    prevProps.action._id === nextProps.action._id &&
    prevProps.isFinishedFight === nextProps.isFinishedFight &&
    prevProps.redCornerColor.r === nextProps.redCornerColor.r &&
    prevProps.redCornerColor.g === nextProps.redCornerColor.g &&
    prevProps.redCornerColor.b === nextProps.redCornerColor.b &&
    prevProps.blueCornerColor.r === nextProps.blueCornerColor.r &&
    prevProps.blueCornerColor.g === nextProps.blueCornerColor.g &&
    prevProps.blueCornerColor.b === nextProps.blueCornerColor.b &&
    prevProps.style === nextProps.style
  );
});
